import {
  LIVE_BREAK_MAX_BUYER_QUEUE_CAP,
  LIVE_BREAK_MAX_OPENER_DELAY_MS,
  LIVE_BREAK_MAX_QUEUE_CAP,
  LIVE_BREAK_MIN_BUYER_QUEUE_CAP,
  LIVE_BREAK_MIN_QUEUE_CAP,
  type LiveBreakBoxIneligibleReason,
  type LiveBreakCloseReason,
  type LiveBreakQueueTier,
  type LiveBreakReleaseReason,
  SPONSORED_STREAM_MAX_SHOW_HOURS,
} from "@loot-os/api-contract";

// Helpers for the live-break staff pages (`…/storefront/live-breaks/…`): the list's filters as the
// URL holds them, the labels the pages print, and the checks the edit form runs before it saves.
// The settings page's helpers (rates, the categories left off) are `liveBreaks.ts`.

// ---- the list's filters, in the URL ------------------------------------------------------------

/** The list's status filter: open (Draining included), Draining alone, or closed; unset = all. */
export const LIVE_BREAK_STATUS_FILTERS = ["open", "draining", "closed"] as const;
export type LiveBreakStatusFilter = (typeof LIVE_BREAK_STATUS_FILTERS)[number];

/** Who runs it: the store (a house live break) or a host; unset = both. */
export const LIVE_BREAK_KIND_FILTERS = ["house", "hosted"] as const;
export type LiveBreakKindFilter = (typeof LIVE_BREAK_KIND_FILTERS)[number];

/** Whether the directory lists it; unset = both. */
export const LIVE_BREAK_VISIBILITY_FILTERS = ["public", "private"] as const;
export type LiveBreakVisibilityFilter = (typeof LIVE_BREAK_VISIBILITY_FILTERS)[number];

export const LIVE_BREAK_STATUS_FILTER_LABELS: Record<LiveBreakStatusFilter, string> = {
  open: "Open",
  draining: "Draining",
  closed: "Closed",
};
export const LIVE_BREAK_KIND_FILTER_LABELS: Record<LiveBreakKindFilter, string> = {
  house: "House",
  hosted: "Hosted",
};
export const LIVE_BREAK_VISIBILITY_FILTER_LABELS: Record<LiveBreakVisibilityFilter, string> = {
  public: "Public",
  private: "Private",
};

/**
 * The list's filters as the URL carries them (`?status=open&kind=house&visibility=public&host=…&q=…`),
 * so a filtered view is a link.
 */
export interface LiveBreaksSearch {
  /** Matches the title, or the host's username or external id. */
  q?: string;
  status?: LiveBreakStatusFilter;
  kind?: LiveBreakKindFilter;
  visibility?: LiveBreakVisibilityFilter;
  /** One host's end user id. */
  host?: string;
}

function oneOf<T extends string>(set: readonly T[], value: unknown): T | undefined {
  return typeof value === "string" && (set as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** The list route's `validateSearch`: an unknown value is dropped, so the filter reads as unset. */
export function parseLiveBreaksSearch(search: Record<string, unknown>): LiveBreaksSearch {
  const host = typeof search.host === "string" ? search.host.trim() : "";
  const q = typeof search.q === "string" ? search.q.trim() : "";
  return {
    q: q ? q.slice(0, 200) : undefined,
    status: oneOf(LIVE_BREAK_STATUS_FILTERS, search.status),
    kind: oneOf(LIVE_BREAK_KIND_FILTERS, search.kind),
    visibility: oneOf(LIVE_BREAK_VISIBILITY_FILTERS, search.visibility),
    host: host && host.length <= 64 ? host : undefined,
  };
}

/**
 * The filters as `liveBreaks.list` takes them. A host filter names hosted live breaks, so it
 * drops a `house` kind rather than ask for a set that is always empty.
 */
export function liveBreaksListFilter(search: LiveBreaksSearch): {
  search?: string;
  status?: LiveBreakStatusFilter;
  isHouse?: boolean;
  isPublic?: boolean;
  hostEndUserId?: string;
} {
  const kind = search.host ? "hosted" : search.kind;
  return {
    search: search.q,
    status: search.status,
    isHouse: kind === undefined ? undefined : kind === "house",
    isPublic: search.visibility === undefined ? undefined : search.visibility === "public",
    hostEndUserId: search.host,
  };
}

/** Which host rows the Hosts page lists: the Host Allow List, or everyone with a host row. */
export const LIVE_BREAK_HOST_LIST_FILTERS = ["allowed", "all"] as const;
export type LiveBreakHostListFilter = (typeof LIVE_BREAK_HOST_LIST_FILTERS)[number];

export const LIVE_BREAK_HOST_LIST_FILTER_LABELS: Record<LiveBreakHostListFilter, string> = {
  allowed: "Host Allow List",
  all: "Every host row",
};

export interface LiveBreakHostsSearch {
  /** Unset = `all`: the page is every host row, the allow-list one view of it. */
  filter?: LiveBreakHostListFilter;
}

/** The Hosts route's `validateSearch`. */
export function parseLiveBreakHostsSearch(search: Record<string, unknown>): LiveBreakHostsSearch {
  return { filter: oneOf(LIVE_BREAK_HOST_LIST_FILTERS, search.filter) };
}

/** The tabs of the Blocks page: one host's set, or the one set every house live break shares. */
export const LIVE_BREAK_BLOCK_SCOPES = ["host", "house"] as const;
export type LiveBreakBlockScope = (typeof LIVE_BREAK_BLOCK_SCOPES)[number];

export interface LiveBreakBlocksSearch {
  scope?: LiveBreakBlockScope;
  /** The host whose set the host tab shows, by end user id. */
  host?: string;
}

/** The Blocks route's `validateSearch`. */
export function parseLiveBreakBlocksSearch(search: Record<string, unknown>): LiveBreakBlocksSearch {
  const host = typeof search.host === "string" ? search.host.trim() : "";
  return {
    scope: oneOf(LIVE_BREAK_BLOCK_SCOPES, search.scope),
    host: host && host.length <= 64 ? host : undefined,
  };
}

// ---- what a live break is doing ----------------------------------------------------------------

/**
 * What a live break is doing, as the list and the detail badge it: closed, Draining (its end has
 * passed or End was pressed), Purchases stopped, Paused, or simply open. Only `open` and `closed`
 * are stored; the rest are read from its settings (`core/src/liveBreaks/liveBreakState.ts`).
 */
export type LiveBreakState = "closed" | "draining" | "stopped" | "paused" | "open";

export const LIVE_BREAK_STATE_LABELS: Record<LiveBreakState, string> = {
  closed: "Closed",
  draining: "Draining",
  stopped: "Purchases stopped",
  paused: "Paused",
  open: "Open",
};

export const LIVE_BREAK_STATE_BADGE: Record<
  LiveBreakState,
  "default" | "secondary" | "outline" | "destructive"
> = {
  open: "default",
  paused: "secondary",
  stopped: "secondary",
  draining: "outline",
  closed: "outline",
};

/** The fields `liveBreakState` reads, as the wire carries them. */
export interface LiveBreakStateFields {
  status: "open" | "closed";
  /** Open and past its end: the server says so as of the read. */
  isDraining: boolean;
  isAcceptingPurchases: boolean;
  isAutoMode: boolean;
  isPaused: boolean;
}

/** Its state, the first that holds of closed, Draining, Purchases stopped, Paused. */
export function liveBreakState(liveBreak: LiveBreakStateFields): LiveBreakState {
  if (liveBreak.status === "closed") return "closed";
  if (liveBreak.isDraining) return "draining";
  if (!liveBreak.isAcceptingPurchases) return "stopped";
  if (liveBreak.isAutoMode && liveBreak.isPaused) return "paused";
  return "open";
}

export const LIVE_BREAK_CLOSE_REASON_LABELS: Record<LiveBreakCloseReason, string> = {
  host_ended: "Ended by its host",
  staff_ended: "Ended by staff",
  end_date: "Its end date passed",
  abandoned: "Abandoned by its host",
  force_closed: "Force closed",
};

export const LIVE_BREAK_RELEASE_REASON_LABELS: Record<LiveBreakReleaseReason, string> = {
  buyer_cancelled: "Cancelled by the buyer",
  staff_cancelled: "Cancelled by staff",
  blocked: "Buyer blocked",
  live_break_closed: "Live break closed",
};

/** Why a box would not sell in a live break now, as a badge says it. */
export const LIVE_BREAK_BOX_INELIGIBLE_LABELS: Record<LiveBreakBoxIneligibleReason, string> = {
  not_openable: "Can't open",
  excluded_category: "Category switched off",
  below_min_price: "Below the minimum price",
  not_in_live_break: "Not in the live break",
};

export const LIVE_BREAK_QUEUE_TIER_LABELS: Record<LiveBreakQueueTier, string> = {
  next: "Next",
  priority: "Priority",
  standard: "Standard",
};

/** A live break's length from creation to close, or to `now` while open: "1 h 25 min", "8 min". */
export function liveBreakDurationText(
  liveBreak: { createdAt: string; closedAt: string | null },
  now: Date,
): string {
  const end = liveBreak.closedAt ? new Date(liveBreak.closedAt) : now;
  const minutes = Math.max(
    0,
    Math.floor((end.getTime() - new Date(liveBreak.createdAt).getTime()) / 60_000),
  );
  const hours = Math.floor(minutes / 60);
  if (hours === 0) return `${minutes} min`;
  return minutes % 60 === 0 ? `${hours} h` : `${hours} h ${minutes % 60} min`;
}

// ---- the edit form's whole-number fields ------------------------------------------------------

/** A whole number typed into a field, in `[min, max]`; null for anything else. */
export function parseWholeNumber(text: string, min: number, max: number): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= min && value <= max ? value : null;
}

/** Queued entries, all buyers: `LIVE_BREAK_MIN_QUEUE_CAP`–`LIVE_BREAK_MAX_QUEUE_CAP`. */
export function parseQueueCap(text: string): number | null {
  return parseWholeNumber(text, LIVE_BREAK_MIN_QUEUE_CAP, LIVE_BREAK_MAX_QUEUE_CAP);
}

/**
 * Queued entries per buyer: blank is no limit (`null`), else
 * `LIVE_BREAK_MIN_BUYER_QUEUE_CAP`–`LIVE_BREAK_MAX_BUYER_QUEUE_CAP`; `undefined` when invalid.
 */
export function parseBuyerQueueCap(text: string): number | null | undefined {
  if (text.trim() === "") return null;
  return (
    parseWholeNumber(text, LIVE_BREAK_MIN_BUYER_QUEUE_CAP, LIVE_BREAK_MAX_BUYER_QUEUE_CAP) ??
    undefined
  );
}

/** The viewers' stage delay, typed in seconds (two decimals at most), as milliseconds. */
export function parseOpenerDelaySeconds(text: string): number | null {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (!match) return null;
  const ms = Number(match[1]) * 1000 + Number((match[2] ?? "").padEnd(2, "0")) * 10;
  return ms <= LIVE_BREAK_MAX_OPENER_DELAY_MS ? ms : null;
}

/** Milliseconds as the delay field shows them: 1500 is "1.5". */
export function openerDelaySecondsText(ms: number): string {
  return String(ms / 1000);
}

/** A sponsored stream's show length, 1–`SPONSORED_STREAM_MAX_SHOW_HOURS` whole hours. */
export function parseShowHours(text: string): number | null {
  return parseWholeNumber(text, 1, SPONSORED_STREAM_MAX_SHOW_HOURS);
}

/** A sponsored stream's priority, a positive whole number. */
export function parseStreamPriority(text: string): number | null {
  return parseWholeNumber(text, 1, 1_000_000);
}

/**
 * A `datetime-local` value as an ISO instant, read in the browser's own zone; null when blank or
 * not a date.
 */
export function localDateTimeToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** An ISO instant as a `datetime-local` value in the browser's own zone, to the minute. */
export function isoToLocalDateTime(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A list's ids with one moved one place up (`-1`) or down (`+1`); unchanged at an edge. */
export function moveId(ids: readonly string[], id: string, by: -1 | 1): string[] {
  const index = ids.indexOf(id);
  const target = index + by;
  if (index < 0 || target < 0 || target >= ids.length) return [...ids];
  const next = [...ids];
  [next[index], next[target]] = [next[target] as string, next[index] as string];
  return next;
}

/** An end user as staff tables name them: `@username`, else their external id. */
export function endUserLabel(user: { username: string | null; externalId: string }): string {
  return user.username ? `@${user.username}` : user.externalId;
}

/**
 * An end user as the staff wire carries them (`StaffEndUserWire`): `@username`, else their
 * external id, else the public name, for one no longer in the environment.
 */
export function staffEndUserLabel(user: {
  username: string | null;
  externalId: string | null;
  displayName: string;
}): string {
  if (user.username) return `@${user.username}`;
  return user.externalId ?? user.displayName;
}
